import {
  type Address,
  type Attachment,
  type BodyType,
  type Connector,
  type CounterDraft,
  type CounterReadyToSend,
  CreateContact,
  type CreateDematRecipient,
  type CreatePatient,
  type CreatePostalRecipient,
  type DashboardSearchParams,
  type DmpConnectorSettings,
  type DocumentConnector,
  type DocumentPatientDto,
  type DocumentRecipientDto,
  type DocumentsList,
  type DocumentsPreview,
  type Encounter,
  ErrorsDashboardSearchParams,
  type GroupValidation,
  type Integration,
  type IntegrationMode,
  type List,
  type NewDocumentPayload,
  type Patient,
  type SendingRequest,
  type SendingRequestBundle,
  type SendingRequestDto,
  type SendingRequestUpdateOperation,
  SendingsDashboardType,
  type SuggestedRecipients,
  type Telecom,
  type UserSettings,
} from '@domain/types';
import { Http } from '@services/http';
import type { DocumentDashboardType } from '@store/documents/documents.state';
import { parseSearchParams } from '@utils/search.util';

const path = '/documents';

export type RemoveVerificationPayload = {
  documentId: string;
  // Only the id is ever read, here and in the optimistic patch. Narrowed from the two v1
  // verification types so a v2 caller can pass one without a cast; both stay assignable.
  verification: { id: number };
};

// UPLOAD

export async function createDocument(document: NewDocumentPayload): Promise<SendingRequestBundle> {
  const formData = new FormData();
  const requestBody: Record<string, any> = {};

  for (const [key, value] of Object.entries(document)) {
    if (key === 'file') {
      if (document.file) formData.append('file', document.file, document.file.name);
    } else {
      requestBody[key] = value;
    }
  }

  formData.append('document', JSON.stringify(requestBody));

  return await Http.getInstance().postMultiFormData({
    path: `${path}`,
    body: formData,
  });
}

// COMMON

export async function fetchDocumentAttachments(
  id: SendingRequest['id'],
  dashboardType: SendingsDashboardType,
): Promise<Attachment[]> {
  return await Http.getInstance().get({
    path: `${path}/${id}/attachments`,
    searchParams: { dashboardType },
  });
}

// OUTBOX DASHBOARDS

const outboxIncludes = {
  includeIntegration: true,
  includeConnectors: true,
};

const draftIncludes = {
  ...outboxIncludes,
  includePredictions: true,
  includeUnselectedConnectors: true,
};

/**
 * The include flags inflate a mutation's response so the caller can adopt it whole. v2 discards the
 * response and re-reads the flat endpoint, so it opts out and the server stops computing
 * predictions and connectors on every write. v1 callers keep the defaults.
 */
export type DraftWriteIncludes = Partial<typeof draftIncludes>;
export const NO_DRAFT_WRITE_INCLUDES: DraftWriteIncludes = {};

const sentIncludes = {
  ...outboxIncludes,
  includePredictions: true,
  includeDeliveryStatuses: true,
};

export const fetchSendingRequests = (searchParams: DashboardSearchParams): Promise<DocumentsList> =>
  Http.getInstance().get({
    path: '/sending_requests',
    searchParams: parseSearchParams(searchParams),
  });

export const fetchSendings = (searchParams: DashboardSearchParams): Promise<DocumentsList> =>
  Http.getInstance().get({
    path: '/sendings',
    searchParams: parseSearchParams(searchParams),
  });

export const fetchSendingRequest = (
  id: SendingRequest['id'],
  dashboardType: DocumentDashboardType,
  isPassive = false,
): Promise<SendingRequestBundle> => {
  let searchParams: DashboardSearchParams = {
    ...outboxIncludes,
  };

  switch (dashboardType) {
    case SendingsDashboardType.Draft:
      searchParams = {
        ...draftIncludes,
      };
      break;
    case SendingsDashboardType.Sent:
      searchParams = {
        ...sentIncludes,
      };
      break;
    default:
      break;
  }

  if (isPassive) {
    searchParams = {
      ...searchParams,
      passive_request: true,
    };
  }

  return Http.getInstance().get({
    path: `/sending_requests/${id}`,
    searchParams: parseSearchParams(searchParams),
  });
};

// DRAFT

// Recipients

export async function addRecipientToDocument(
  docId: SendingRequest['id'],
  recipient: DocumentRecipientDto,
  includes: DraftWriteIncludes = draftIncludes,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/recipients`,
    body: { recipient, ...includes },
  });
}

export async function addRecipientsToDocument(
  docId: SendingRequest['id'],
  recipients: DocumentRecipientDto[],
  includes: DraftWriteIncludes = draftIncludes,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/recipients`,
    body: { recipients, ...includes },
  });
} // suggestedrecipients

export async function updateDocumentRecipient(
  docId: SendingRequest['id'],
  recipient: DocumentRecipientDto,
  includes: DraftWriteIncludes = draftIncludes,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().put({
    path: `${path}/${docId}/recipients`,
    body: { recipient, ...includes },
  });
}

export async function removeRecipientFromDocument(
  docId: SendingRequest['id'],
  recipient: DocumentRecipientDto,
  trustedContactId?: string,
  includes: DraftWriteIncludes = draftIncludes,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().destroyWithBody({
    path: `${path}/${docId}/recipients`,
    body: { recipient, trustedContactId, ...includes },
  });
}

export async function updateDocumentWithPostalRecipient(
  docId: SendingRequest['id'],
  recipient: CreatePostalRecipient,
  includes: DraftWriteIncludes = draftIncludes,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/recipients`,
    body: { recipient, ...includes },
  });
}

export async function updateDocumentWithDematRecipient(
  docId: SendingRequest['id'],
  patch: CreateDematRecipient,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/recipients`,
    body: { recipient: patch, ...draftIncludes },
  });
}

export async function addPatientRecipientToDocument(
  docId: SendingRequest['id'],
  documentPatient: Omit<DocumentPatientDto, 'verifications'>,
  shouldRefresh = false,
  trustedContacts?: Array<{
    id: string;
    telecoms: Telecom[];
    addresses: Address[];
  }>,
): Promise<SendingRequestBundle> {
  const body: any = {
    patient: documentPatient,
    shouldRefresh,
    ...draftIncludes,
  };

  if (trustedContacts) {
    body.trustedContacts = trustedContacts;
  }

  return await Http.getInstance().put({
    path: `${path}/${docId}/patientRecipient`,
    body,
  });
}

export async function removePatientRecipientFromDocument(
  docId: SendingRequest['id'],
): Promise<SendingRequestBundle> {
  return await Http.getInstance().destroyWithBody({
    path: `${path}/${docId}/patientRecipient`,
    body: { ...draftIncludes },
  });
}

// Patient
export async function createOrAddDocumentPatient(
  docId: SendingRequest['id'],
  // Before the refacto we had addDocument patient and createDocumentPatient that use the same API endpoint
  // I've merge both, but the API doesn't provide a proper list of whitelisted params
  // todo: merge both type into a single one, if id is present then it doesn't create the patient in FHIR.
  patient: CreatePatient | DocumentPatientDto,
  shouldRefresh = false,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/patient`,
    body: { patient, shouldRefresh, ...draftIncludes },
  });
}

export async function removeDocumentPatient(
  docId: SendingRequest['id'],
): Promise<SendingRequestBundle> {
  return await Http.getInstance().destroyWithBody({
    path: `${path}/${docId}/patient`,
    body: { ...draftIncludes },
  });
}

export async function updateDocumentPatient(docId: SendingRequest['id'], patient: Patient) {
  return await Http.getInstance().put({
    path: `${path}/${docId}/patient`,
    body: { patient, ...draftIncludes },
  });
} // return patient, patient and needsRefresh

export async function toggleConnector({
  documentId,
  integrationId,
  connectorId,
  integrationMode,
  includes = draftIncludes,
}: {
  documentId: SendingRequest['id'];
  integrationId: Integration['id'];
  connectorId: DocumentConnector['id'];
  integrationMode?: IntegrationMode;
  includes?: DraftWriteIncludes;
}): Promise<SendingRequestBundle> {
  return await Http.getInstance().put({
    path: `${path}/${documentId}/integrations/${integrationId}/connectors/${connectorId}/toggle`,
    body: {
      integrationMode,
      integrationId,
      ...includes,
    },
  });
}

export async function updateConnectorSettings({
  documentId,
  integrationId,
  connectorId,
  settings,
  includes = draftIncludes,
}: {
  documentId: SendingRequest['id'];
  integrationId: Integration['id'];
  connectorId: DocumentConnector['id'];
  settings: DmpConnectorSettings;
  includes?: DraftWriteIncludes;
}): Promise<SendingRequestBundle> {
  return await Http.getInstance().put({
    path: `${path}/${documentId}/integrations/${integrationId}/connectors/${connectorId}/settings`,
    body: { ...settings, ...includes },
  });
}

export async function toggleConnectorIntegrationMode({
  connectorId,
  documentId,
  fallbackReference,
  integrationId,
  integrationMode,
  includes = draftIncludes,
}: {
  connectorId: DocumentConnector['id'];
  documentId: SendingRequest['id'];
  fallbackReference: string;
  integrationId: Integration['id'];
  integrationMode: IntegrationMode;
  includes?: DraftWriteIncludes;
}): Promise<SendingRequestBundle> {
  return await Http.getInstance().put({
    path: `${path}/${documentId}/integrations/${integrationId}/connectors/${connectorId}/toggle_fallback`,
    body: { fallbackReference, integrationMode, ...includes },
  });
}

export async function fetchEncounters(
  docId: SendingRequest['id'],
  integrationId: Integration['id'],
): Promise<List<Encounter>> {
  return await Http.getInstance().get({
    path: `${path}/${docId}/integrations/${integrationId}/encounters`,
  });
}

export async function replaceEncounter(
  docId: SendingRequest['id'],
  integrationId: Integration['id'],
  encounter: Encounter,
) {
  return await Http.getInstance().put({
    path: `${path}/${docId}/integrations/${integrationId}/encounters`,
    body: encounter as unknown as BodyType,
  });
}

export async function refreshPatientMatch(docId: SendingRequest['id']): Promise<undefined> {
  return await Http.getInstance().get({ path: `${path}/${docId}/integrations/refresh` });
}

// Actions on documents

export async function sendDocument(id: SendingRequest['id']): Promise<SendingRequestDto> {
  return await Http.getInstance().put({
    path: `${path}/${id}/send`,
    body: { ...draftIncludes },
  });
} // useless response payload

interface SendDocumentsResponse {
  id: string;
  success: boolean;
}
export async function sendDocuments(ids: SendingRequest['id'][]): Promise<SendDocumentsResponse[]> {
  return await Http.getInstance().post({
    path: `${path}/send`,
    body: { ids, ...draftIncludes },
  });
}

export async function downloadDocuments(
  ids: SendingRequest['id'][],
  dashboardType: SendingsDashboardType,
  sendOnlyDocument = false,
): Promise<void> {
  return await Http.getInstance().download(
    {
      path: `${path}/download`,
      body: { ids, dashboardType, sendOnlyDocument },
    },
    'POST',
  );
}

export async function previewDocuments(ids: SendingRequest['id'][]): Promise<DocumentsPreview> {
  return await Http.getInstance().get({
    path: `${path}/previews`,
    searchParams: { ids },
  });
}

export async function validateGroupDocuments(
  ids: SendingRequest['id'][],
): Promise<GroupValidation> {
  return await Http.getInstance().post({
    path: `${path}/validateGroup`,
    body: { ids },
  });
}

export async function groupDocuments(ids: SendingRequest['id'][]): Promise<{ id: string }> {
  return await Http.getInstance().post({
    path: `${path}/group`,
    body: { ids },
  });
}

export async function splitDocuments(ids: SendingRequest['id'][]): Promise<{ ids: string[] }> {
  return await Http.getInstance().destroyWithBody({
    path: `${path}/group`,
    body: { ids },
  });
}

export async function splitDocument(id: SendingRequest['id']): Promise<void> {
  return await Http.getInstance().destroy({
    path: `${path}/${id}/group`,
  });
}

export async function splitSendingRequestDocument(
  documentId: string,
  payload: any,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${documentId}/splitPdf`,
    body: { ...payload, ...draftIncludes },
  });
}

export async function duplicateDocument(id: SendingRequest['id']): Promise<SendingRequestDto> {
  return await Http.getInstance().post({
    path: `${path}/${id}/duplicate`,
    body: { ...draftIncludes },
  });
}

export async function duplicateDocuments(ids: SendingRequest['id'][]): Promise<{ ids: string[] }> {
  return await Http.getInstance().post({
    path: `${path}/duplicate`,
    body: { ids },
  });
}

export const cancelSendingRequests = (ids: SendingRequest['id'][]): Promise<void> =>
  Http.getInstance().post({
    path: '/documents/cancel',
    body: { ids },
  });

export async function updateDocument(
  docId: SendingRequest['id'],
  patch: SendingRequestUpdateOperation,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().put({
    path: `${path}/${docId}`,
    body: { ...patch, ...draftIncludes },
  });
}

export async function deleteTrustedContact(
  documentId: SendingRequest['id'],
  trustedContactId: string,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().destroy({
    path: `${path}/${documentId}/trusted_contacts/${trustedContactId}`,
  });
}

export async function addTrustedContact(
  docId: SendingRequest['id'],
  contact: CreateContact,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/trusted_contacts`,
    body: { trusted_contact: contact },
  });
}

export async function updateTrustedContact(
  docId: SendingRequest['id'],
  trustedContactId: string,
  contact: CreateContact,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().patch({
    path: `${path}/${docId}/trusted_contacts/${trustedContactId}`,
    body: { trusted_contact: contact },
  });
}

// Other

export async function fetchSuggestedRecipients(
  id: SendingRequest['id'],
): Promise<SuggestedRecipients> {
  return await Http.getInstance().get({
    path: `${path}/${id}/suggestedRecipients`,
  });
}

export async function setCustomEnsMessage(
  documentId: SendingRequest['id'],
  message: string,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${documentId}/customPatientEnsMessage`,
    body: { message, ...draftIncludes },
  });
}

export async function setEnsConversationStatus(
  documentId: SendingRequest['id'],
  closed: boolean,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${documentId}/ensConversationStatus`,
    body: { closed, ...draftIncludes },
  });
}

export async function removeVerification(data: RemoveVerificationPayload) {
  return await Http.getInstance().put<SendingRequestBundle>({
    path: `${path}/${data.documentId}/verifications/${data.verification.id}/dismiss`,
  });
}

export async function fetchTotalReadyToSend(): Promise<CounterReadyToSend> {
  return await Http.getInstance().get({ path: `${path}/counter/readyToSendCounter` });
}

export async function fetchTotalDraftCounter(): Promise<CounterDraft> {
  return await Http.getInstance().get({ path: `${path}/counter/draftsCounter` });
}

// SENT

// Integrations

export async function cancelDmpDocument(
  documentId: SendingRequest['id'],
  integrationId: Integration['id'],
  connectorId: Connector['id'],
): Promise<SendingRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${documentId}/integrations/${integrationId}/connectors/${connectorId}/cancel`,
    body: { ...sentIncludes },
  });
}

export async function replaceDmpDocument(
  documentId: SendingRequest['id'],
  integrationId: Integration['id'],
  connectorId: Connector['id'],
  file: File,
  settings: DmpConnectorSettings,
  userSettings: UserSettings,
): Promise<SendingRequestBundle> {
  const formData = new FormData();

  formData.append('file', file, file.name);
  formData.append('settings', JSON.stringify(settings));
  formData.append('userSettings', JSON.stringify(userSettings));

  const includes = Object.keys(sentIncludes) as (keyof typeof sentIncludes)[];
  for (const key of includes) {
    formData.append(key, String(sentIncludes[key]));
  }

  return await Http.getInstance().postMultiFormData({
    path: `${path}/${documentId}/integrations/${integrationId}/connectors/${connectorId}/replace`,
    body: formData,
  });
}

// Other
export async function replaceMssDocument(
  documentId: SendingRequest['id'],
  file: File,
  telecoms: string[],
): Promise<SendingRequestBundle> {
  const formData = new FormData();

  formData.append('file', file, file.name);
  for (const telecom of telecoms) {
    formData.append('telecoms[]', telecom);
  }

  const includes = Object.keys(sentIncludes) as (keyof typeof sentIncludes)[];
  for (const key of includes) {
    formData.append(key, String(sentIncludes[key]));
  }

  return await Http.getInstance().postMultiFormData({
    path: `${path}/${documentId}/replace`,
    body: formData,
  });
}

// ERRORS

export async function markDocumentAsRead(documentId: SendingRequest['id']) {
  await Http.getInstance().put({
    path: `${path}/${documentId}/read`,
  });
}

export async function markDocumentAsUnread(documentId: SendingRequest['id']) {
  await Http.getInstance().destroy({
    path: `${path}/${documentId}/read`,
  });
}

export async function markAllAsRead() {
  await Http.getInstance().put({
    path: `${path}/read`,
  });
}

export async function fetchErroredDocuments(
  searchParams: ErrorsDashboardSearchParams,
): Promise<DocumentsList> {
  return await Http.getInstance().get({ path: `${path}/errored`, searchParams });
}

// FORWARD (Inbox and Sent)
export async function forwardDocument(
  documentId: SendingRequest['id'],
  dashboardType: SendingsDashboardType,
  userSettings: UserSettings,
  selectedIdentityReference?: string,
): Promise<{ document: SendingRequestBundle['document'] }> {
  return await Http.getInstance().post({
    path: `${path}/forward`,
    body: { documentId, selectedIdentityReference, dashboardType, userSettings, ...draftIncludes },
  });
}

// INBOX DASHBOARDS

export async function fetchDocument(
  id: SendingRequest['id'],
  dashboardType: SendingsDashboardType,
): Promise<SendingRequestBundle> {
  return await Http.getInstance().get({ path: `${path}/${id}`, searchParams: { dashboardType } });
}

export async function fetchDocuments(searchParams: DashboardSearchParams): Promise<DocumentsList> {
  return await Http.getInstance().get({ path, searchParams: parseSearchParams(searchParams) });
}

// INBOX

export async function archiveDocument(id: SendingRequest['id']): Promise<void> {
  return await Http.getInstance().put({
    path: `${path}/${id}/archive`,
  });
}

export async function archiveDocuments(ids: SendingRequest['id'][]): Promise<void> {
  return await Http.getInstance().put({
    path: `${path}/archive`,
    body: { ids },
  });
}

// ARCHIVE

export async function restoreDocument(id: SendingRequest['id']): Promise<void> {
  return await Http.getInstance().put({
    path: `${path}/${id}/restore`,
  });
}

export async function restoreDocuments(ids: SendingRequest['id'][]): Promise<void> {
  return await Http.getInstance().put({
    path: `${path}/restore`,
    body: { ids },
  });
}
